import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { ShieldOff, ArrowLeft, LayoutDashboard } from 'lucide-react';

// Wave 3.99h — Route-level RBAC guard. Renders its ``children`` only if
// the current user has the required permission; otherwise renders a
// friendly "no access" screen with Back and Dashboard escape hatches.
//
// Usage in App.js::
//
//   <Route path="/logs" element={
//     <PermissionGate permission="logs.view">
//       <Logs />
//     </PermissionGate>
//   } />
//
// Super Admin and Company Admin always pass (mirrors the backend rule).
// If a user has no permissions at all, they can still see Dashboard
// (which is not gated), so the escape route always works.

export function PermissionGate({ permission, anyOf, moduleLabel, allowSales = false, children }) {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();

  // Not logged in — the outer <Protected> route already redirects to
  // /login, but guard defensively anyway.
  if (!user) return null;

  // Sales roles (Sales Person / Sales Manager) are customer-scoped and may
  // only reach routes explicitly flagged ``allowSales`` (Dashboard is
  // ungated). Everything else returns the friendly "restricted" screen so a
  // guessed URL can't expose non-permitted modules.
  const isSalesRole = user.role === 'sales_person' || user.role === 'sales_manager';
  if (isSalesRole) {
    if (allowSales) return children;
    return _deniedScreen(moduleLabel || permission || 'this page', navigate, permission);
  }

  // Wave 3.99l — Support an ``anyOf`` array so a route can be opened
  // whenever the user has at least one of a set of related keys (e.g.
  // the Reports page opens for any per-report sub-key).
  const passes = anyOf && anyOf.length > 0
    ? anyOf.some((k) => hasPermission(k))
    : hasPermission(permission);
  if (passes) {
    return children;
  }

  return _deniedScreen(moduleLabel || permission || 'this page', navigate, permission);
}

function _deniedScreen(humanKey, navigate, permission) {
  return (
    <div
      className="min-h-[60vh] flex items-center justify-center p-6"
      data-testid="permission-gate-denied"
      data-permission-key={permission}
    >
      <div className="max-w-md w-full text-center bg-white/70 backdrop-blur-sm border border-[var(--divider)] rounded-xl p-8 shadow-sm">
        <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
          <ShieldOff size={26} className="text-amber-600" strokeWidth={1.6} />
        </div>
        <h1 className="text-lg font-semibold text-slate-800 mb-1">Access restricted</h1>
        <p className="text-sm text-slate-600">
          You don&apos;t have permission to view <span className="font-medium text-slate-800">{humanKey}</span>.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          If you need access, please ask your admin to grant it under
          <span className="font-mono bg-slate-100 rounded px-1 mx-1">Users → Permissions</span>.
        </p>

        <div className="mt-6 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="permission-gate-back"
          >
            <ArrowLeft size={14} className="mr-1.5" /> Back
          </Button>
          <Button
            size="sm"
            className="luxury-button"
            onClick={() => navigate('/dashboard')}
            data-testid="permission-gate-dashboard"
          >
            <LayoutDashboard size={14} className="mr-1.5" /> Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PermissionGate;
